import { useMemo } from 'react';
import { Link } from 'react-router';
import type { PerspectiveOrder } from '~/api/order';
import {
  useGetOrdersByPerspective,
  useGetOrdersCountByPerspective,
} from '~/api';
import {
  calculateOrderTotal,
  formatCurrency,
  formatOrderDisplayId,
} from '~/utils';
import type { TradePerspective } from '~/graphql/graphql';
import {
  DashboardColumn,
  DashboardColumnSpinner,
} from '~/pages/dashboard/sections/DashboardColumn';

export type Order = PerspectiveOrder;

interface PendingOrdersColumnProps {
  perspective: TradePerspective;
  title: string;
  subtitle: string;
  footerLink: string;
  footerLabel: string;
  emptyText: string;
  errorText: string;
  getOrgLabel: (order: Order) => string;
  renderRow3Right: (order: Order) => React.ReactNode;
  renderRow4Right?: (order: Order) => React.ReactNode;
}

export function PendingOrdersColumn({
  perspective,
  title,
  subtitle,
  footerLink,
  footerLabel,
  emptyText,
  errorText,
  getOrgLabel,
  renderRow3Right,
  renderRow4Right,
}: Readonly<PendingOrdersColumnProps>) {
  const { orders, isLoading, isError } = useGetOrdersByPerspective(
    perspective,
    'PENDING_SELLER_CONFIRMATION'
  );
  const { counts } = useGetOrdersCountByPerspective(perspective);

  const pendingCount = counts?.pendingSellerConfirmation;

  const sortedOrders = useMemo(() => {
    if (!orders) return [];
    return [...orders]
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      )
      .slice(0, 5);
  }, [orders]);

  let body: React.ReactNode;

  if (isLoading) {
    body = <DashboardColumnSpinner />;
  } else if (isError) {
    body = (
      <p className="text-sm font-medium text-neut-sec text-center px-6 py-8">
        {errorText}
      </p>
    );
  } else if (sortedOrders.length === 0) {
    body = (
      <p className="text-sm font-medium text-neut-sec px-6 py-8">{emptyText}</p>
    );
  } else {
    body = sortedOrders.map((order) => {
      const primaryItem = order.items[0];
      const primaryProductName =
        primaryItem?.platformProductsLengths?.platformProduct?.name;
      const total = calculateOrderTotal(order);
      const currency = order.currency ?? primaryItem?.effectivePrice?.currency;

      return (
        <div
          key={order.id}
          className="flex flex-col gap-1 px-6 py-4 border-b border-neut-border last:border-b-0"
        >
          {/* Row 1: order ID + total */}
          <div className="flex items-center justify-between">
            <Link
              to={`/orders/view/${order.id}?returnPath=/dashboard`}
              className="text-sm font-medium text-prim hover:text-prim-hover"
            >
              #{formatOrderDisplayId(order)}
            </Link>
            <span className="text-sm font-medium text-neut-prim">
              {total != null && total > 0
                ? formatCurrency(total, currency)
                : '—'}
            </span>
          </div>
          {/* Row 2: org name */}
          <p className="text-xs font-medium text-neut-sec">
            {getOrgLabel(order)}
          </p>
          {primaryItem && (
            <>
              {/* Row 3: product name + row3Right (clock+time or action button) */}
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-neut-prim">
                  {primaryProductName ?? '—'}
                </p>
                {renderRow3Right(order)}
              </div>
              {/* Row 4: quantity + optional row4Right (time-ago for seller) */}
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-neut-sec">
                  {primaryItem.quantity.value} {primaryItem.quantity.unit}
                </p>
                {renderRow4Right?.(order)}
              </div>
            </>
          )}
        </div>
      );
    });
  }

  return (
    <DashboardColumn
      title={title}
      subtitle={subtitle}
      badge={pendingCount}
      footerLink={footerLink}
      footerLabel={footerLabel}
    >
      {body}
    </DashboardColumn>
  );
}
